import './terms.component.scss';

// @vendor
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// @components
import { OKLayoutMigration } from '../layout/OKLayoutMigration';
import PreContractualDocumentViewer from 'components/commons/preContractualDocumentViewer/preContractualDocumentViewer';
import { OKCheckbox } from 'commonsComponents/OKCheckbox';
import { SignaturePattern } from 'components/signaturePattern/signaturePattern';
import { CommonError } from '../commonError/commonError.component';

// @ helpers
const { formatText, FormattedText } = require('core/i18n').i18n;
import { useTerms } from './hooks/useTerms';
import { recordLinkV2 } from 'utilities/tagging';

// @ constants
import { termsKeys } from './terms.component.poeditor';
import { analyticsId, catalogue } from './analytics/terms.tracking';
import {
  DATA_PROTECTION_PATH,
  CALL_STATUS,
  SIGNATURE_PATTERN_OPERATION_TYPE,
  VIEWER_HEIGHT,
} from '../../migrationUsersBranchView.constants';
import { getDocumentLink, isItWebViewPlatform } from '../../utilities/migration.utils';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import MbPdfHybrid from '../pdfHybrid/mb-pdf-hybrid/mb-pdf-hybrid';
import useScrollPercentage from '../../../contractsView/components/documentation/hooks/useScrollPercentage';

export const Terms = ({ handleExitLeaveModal, handleChangeMigrationState }) => {
  const {
    contratualInfoChecked,
    dataProtectionChecked,
    documentReaded,
    documentsStatus,
    error,
    handleDocumentReaded,
    handleFinish,
    handleFinishRetry,
    optionalConsentChecked,
    sendConsentsStatus,
    sendDataAndGetDocs,
    signaturePattern,
    toggleCheckContractualInfo,
    toggleCheckDataProtection,
    toggleOptionalConsent,
  } = useTerms(handleChangeMigrationState);
  const [pdfContainerRef] = useScrollPercentage();

  const history = useHistory();
  const immScreen = useSelector(state => state.screen);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const immContractualPolling = useSelector(state => state.contractualPolling);

  const preContractInfo = {
    preContractPdfFileName: formatText(termsKeys.preContractPdfFileName),
  };
  const contractualError = immContractualPolling.get('documents').get('error');
  const handleClick = e => {
    e?.preventDefault();
    if (isItWebViewPlatform()) {
      setIsPdfOpen(true);
    } else {
      window.open(getDocumentLink(DATA_PROTECTION_PATH), '_system');
    }
  };

  const handleScroll = ({ hasScrolledToBottom }) => {
    if (hasScrolledToBottom) {
      handleDocumentReaded();
    }
  };

  const onModalClose = () => {
    setIsPdfOpen(false);
  };

  const handleContinue = () => {
    recordLinkV2(catalogue[analyticsId.CONTINUE_BUTTON](optionalConsentChecked));
    handleFinish();
  };

  return (
    <>
      <OKLayoutMigration
        header={{
          leftSideText: formatText(termsKeys.headerLeftText),
          exitButton: { handleClickButton: handleExitLeaveModal },
        }}
        footer={
          [sendConsentsStatus, documentsStatus].includes(CALL_STATUS.ERROR) || contractualError
            ? undefined
            : {
                smallMarginTop: true,
                leftSideLink: {
                  text: formatText(termsKeys.cancelLink),
                  handler: () => {
                    recordLinkV2(catalogue[analyticsId.CANCEL_LINK]());
                    if (immScreen.get('desktop')) {
                      history.push('/myprofile/global');
                    } else {
                      window.location.href = '/myprofile/global/';
                    }
                  },
                },
                rightSideButton: {
                  loading:
                    signaturePattern.get('isFetchingSMSCode') ||
                    sendConsentsStatus === CALL_STATUS.IN_PROGRESS,
                  disabled:
                    signaturePattern.get('isFetchingSMSCode') ||
                    signaturePattern.get('isFetchingConfig') ||
                    !signaturePattern.get('isFetchingConfigSuccess') ||
                    signaturePattern.get('showWarningSMS') ||
                    (signaturePattern.get('requiredSMS') &&
                      signaturePattern.get('inputValueSMS')?.length !== 4) ||
                    sendConsentsStatus === CALL_STATUS.IN_PROGRESS,
                  text: formatText(termsKeys.continueButton),
                  handler: handleContinue,
                },
              }
        }
      >
        {[sendConsentsStatus, documentsStatus].includes(CALL_STATUS.ERROR) || contractualError ? (
          <CommonError
            error={error?.error?.description?.description || error?.error?.description}
            handleOnClick={documentReaded ? handleFinishRetry : sendDataAndGetDocs}
          />
        ) : (
          <div className={`${documentReaded ? '' : 'custom-pdf-container'}`}>
            <div className="migration-terms__title">
              <FormattedText value={termsKeys.title} />
            </div>
            <PreContractualDocumentViewer
              withoutDownload={isItWebViewPlatform()}
              displayScrollButton
              preContractDocumentInfo={preContractInfo}
              customTitle={formatText(termsKeys.viewerDescription)}
              documentReadedCallback={handleDocumentReaded}
              hasPolling
              viewerHeight={
                immScreen.get('isMobile') ? VIEWER_HEIGHT.MOBILE : VIEWER_HEIGHT.DEFAULT
              }
              withoutPrint={immScreen.get('isMobile')}
              retryButtonAnalitycs={catalogue[analyticsId.RETRY_BUTTON_DOCUMENT_VIEWER]()}
              withScrollableDocument
              pdfContainerRef={pdfContainerRef}
              handleScroll={handleScroll}
              isMobile={immScreen.get('isMobile')}
            />
            {documentReaded && (
              <div className="migration-terms__checkboxes">
                <OKCheckbox
                  idName="migration-contractual-info"
                  onChange={toggleCheckContractualInfo}
                  label={<FormattedText value={termsKeys.contractualInfoCheckboxText} />}
                  value={contratualInfoChecked}
                />
                <OKCheckbox
                  idName="migration-data-protection"
                  onChange={toggleCheckDataProtection}
                  label={
                    <>
                      <FormattedText value={termsKeys.dataProtectionCheckboxText} />
                      <a onClick={handleClick}>{formatText(termsKeys.dataProtectionLink)}</a>
                      <FormattedText value={termsKeys.dataProtectionCheckboxText2} />
                    </>
                  }
                  value={dataProtectionChecked}
                />
                <OKCheckbox
                  idName="migration-optional-consent"
                  onChange={toggleOptionalConsent}
                  label={<FormattedText value={termsKeys.optionalConsentCheckboxText} />}
                  value={optionalConsentChecked}
                />
              </div>
            )}
            {contratualInfoChecked && dataProtectionChecked ? (
              <section className="migration-terms__signature-section">
                <SignaturePattern
                  operationType={SIGNATURE_PATTERN_OPERATION_TYPE}
                  operationParams={{}}
                  customEventTag="interaccion en migracion"
                  customCategoryError="incorrectCode"
                  taggingOnKeyPress={() => recordLinkV2(catalogue[analyticsId.KEY_PRESS_OTP]())}
                  onResendClick={() =>
                    recordLinkV2(catalogue[analyticsId.CLICK_REQUEST_NEW_OTP_CODE]())
                  }
                />
              </section>
            ) : null}
          </div>
        )}
      </OKLayoutMigration>
      <MbPdfHybrid
        isOpen={isPdfOpen}
        file={getDocumentLink(DATA_PROTECTION_PATH)}
        filetype={'url'}
        filename={preContractInfo.preContractPdfFileName}
        title={preContractInfo.preContractPdfFileName}
        onClose={onModalClose}
        linkText={termsKeys.readyLink}
      />
    </>
  );
};

Terms.propTypes = {
  handleExitLeaveModal: PropTypes.func.isRequired,
};
